/**
 * StoryCard — P9 Sweet Spot + WhatsApp Story Card.
 * Emotional primary output. Designed to be screenshot-worthy and shareable.
 */
import { Share2, Trophy, Sparkles } from "lucide-react";
import { inr } from "../../lib/format";

export default function StoryCard({ result }) {
  if (!result) return null;
  const { sweet_spot_year, sweet_spot_month, year20_net_wealth, total_terminal_equity,
          per_property = [], velocity_score = {}, horizon_years } = result;
  const purchased = per_property.filter((p) => p.purchased);
  const propsLine = purchased.length === 1
    ? "1 property"
    : `${purchased.length} properties`;

  const handleShare = () => {
    const text = [
      `🏡 My Rental Snowball plan (Estima)`,
      ``,
      sweet_spot_year != null
        ? `Sweet spot: Year ${sweet_spot_year} — portfolio funds itself.`
        : `Sweet spot: not reached in ${horizon_years}y horizon (need to tweak).`,
      `Net wealth in Year ${Math.round(horizon_years)}: ${inr(year20_net_wealth)}`,
      `Across ${propsLine}.`,
      `Velocity: ${velocity_score.score || 0}/100 (${velocity_score.label || "—"})`,
      ``,
      `Plan yours: estima.co.in/calculators?tab=odcf`,
    ].join("\n");
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };

  const heroBg = sweet_spot_year != null
    ? "linear-gradient(135deg, #0B7B6E 0%, #084C44 100%)"
    : "linear-gradient(135deg, #C85A32 0%, #8A3B1E 100%)";

  return (
    <div
      className="rounded-xl p-7 text-white relative overflow-hidden"
      style={{ background: heroBg }}
      data-testid="snowball-story-card"
    >
      <div className="absolute -right-6 -top-6 opacity-10 text-9xl pointer-events-none select-none">
        🏡
      </div>
      <div className="relative">
        <div className="flex items-center gap-2 mb-2 text-white/80 text-xs uppercase tracking-widest">
          <Sparkles size={14} />
          Your Snowball Story
        </div>

        {sweet_spot_year != null ? (
          <>
            <h3 className="font-serif text-4xl mb-1" data-testid="story-sweet-spot">
              Year {sweet_spot_year}
            </h3>
            <p className="text-white/90 text-lg mb-5">
              is when your portfolio starts funding itself.
            </p>
          </>
        ) : (
          <>
            <h3 className="font-serif text-3xl mb-1">No sweet spot — yet</h3>
            <p className="text-white/90 text-base mb-5">
              In your current plan, the portfolio never turns net cashflow positive.
              Try the live sliders below to find a path.
            </p>
          </>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Metric label={`Year ${Math.round(horizon_years)} wealth`} value={inr(year20_net_wealth)} />
          <Metric label="Terminal equity" value={inr(total_terminal_equity)} />
          <Metric label="Properties" value={purchased.length} />
          <Metric label="Velocity" value={`${velocity_score.score || 0}`} sub="/ 100" />
        </div>

        <button
          onClick={handleShare}
          className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur transition-colors text-white px-5 py-2.5 rounded-md text-sm font-medium"
          data-testid="snowball-whatsapp-share"
        >
          <Share2 size={14} />
          Share to WhatsApp
        </button>
      </div>
    </div>
  );
}

function Metric({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/70 mb-0.5">{label}</div>
      <div className="font-serif text-xl">
        {value}{sub && <span className="text-sm opacity-70 ml-0.5">{sub}</span>}
      </div>
    </div>
  );
}
